import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { absencesAPI } from '../services/api';
import toast from 'react-hot-toast';
import { ChevronLeft, Pencil, XCircle, Download } from 'lucide-react';
import { exportAbsencePDF } from '../utils/exportPDF';
import './Absences.css';

const TYPE_LABELS = {
  CONGE_PAYE:       'Congé payé',
  CONGE_SANS_SOLDE: 'Congé sans solde',
  RTT:              'RTT',
};

const STATUT_META = {
  BROUILLON: { label: 'Brouillon', cls: 'abs-badge--brouillon' },
  SOUMIS:    { label: 'Soumis',    cls: 'abs-badge--soumis' },
  VALIDE:    { label: 'Validé',    cls: 'abs-badge--valide' },
  REFUSE:    { label: 'Refusé',    cls: 'abs-badge--refuse' },
  ANNULE:    { label: 'Annulé',    cls: 'abs-badge--annule' },
};

const fmt = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';

export default function AbsenceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [req, setReq]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    absencesAPI.list({})
      .then(r => {
        const found = (r.data || []).find(x => x.id === id);
        setReq(found || null);
      })
      .catch(() => toast.error('Erreur chargement'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleCancel = async () => {
    if (!window.confirm('Annuler cette demande ?')) return;
    try {
      await absencesAPI.cancel(id);
      toast.success('Demande annulée');
      navigate('/absences');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  if (loading) return <div className="loading-state"><div className="spinner"/></div>;
  if (!req) return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Demande introuvable.</div>;

  const sm = STATUT_META[req.statut] || STATUT_META.BROUILLON;

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/absences')}>
              <ChevronLeft size={16}/>
            </button>
            <div>
              <h1 style={{ margin: 0 }}>Demande de congé</h1>
              <p style={{ margin: 0 }}>{TYPE_LABELS[req.type_conge]}</p>
            </div>
          </div>
        </div>
        <div className="page-header-actions">
          <span className={`abs-badge ${sm.cls}`} style={{ fontSize: '0.825rem', padding: '5px 14px' }}>
            {sm.label}
          </span>
          {['VALIDE', 'REFUSE'].includes(req.statut) && (
            <button className="btn btn-secondary btn-sm" onClick={() => exportAbsencePDF(req)} title="Télécharger PDF">
              <Download size={14}/> PDF
            </button>
          )}
        </div>
      </div>

      <div className="card" style={{ maxWidth: 600 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Row label="Type de congé"   value={TYPE_LABELS[req.type_conge]} />
          <Row label="Date de début"   value={fmt(req.date_debut)} />
          <Row label="Date de fin"     value={fmt(req.date_fin)} />
          <Row label="Nombre de jours" value={<strong style={{ color: 'var(--primary)' }}>{req.nb_jours} jour{req.nb_jours > 1 ? 's' : ''}</strong>} />
          {req.depart_midi && <Row label="Départ" value="À 12h (demi-journée)" />}
          {req.retour_midi  && <Row label="Retour" value="À 14h (demi-journée)" />}
          {req.description  && <Row label="Description" value={req.description} />}
          {req.commentaire_rh && (
            <div style={{
              background: 'rgba(239,68,68,0.06)',
              border: '1px solid rgba(239,68,68,0.2)',
              borderRadius: 8, padding: '10px 14px',
              fontSize: '0.8rem', color: '#ef4444',
            }}>
              <strong>Commentaire RH :</strong> {req.commentaire_rh}
            </div>
          )}
        </div>

        {['BROUILLON', 'SOUMIS'].includes(req.statut) && (
          <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
            {req.statut === 'BROUILLON' && (
              <button className="btn btn-secondary" onClick={() => navigate(`/absences/${id}/edit`)}>
                <Pencil size={14}/> Modifier
              </button>
            )}
            <button className="btn btn-ghost" style={{ color: '#f59e0b' }} onClick={handleCancel}>
              <XCircle size={14}/> Annuler la demande
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: 10 }}>
      <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>{label}</span>
      <span style={{ fontSize: '0.875rem' }}>{value}</span>
    </div>
  );
}
